import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FullCalendar from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import { getSudentCalendarInfo, getTutorCalendarInfo } from '../../store/appointment/appointmentActions';
import { getDeviceTimeZone } from '../../utils';
import { COURSE_DETAILS_URL } from '../../components/app/routes';
import { ProfileRole } from '../../store/profile/profileSlice';
import { CalendarColorLegend } from '../../components/calendar/CalendarColorLegend';
import { CalendarEventContent } from '../../components/calendar/CalendarEventContent';

const getLessonColors = (isFirstLesson) => {
    if (isFirstLesson) {
        return { color: '#FFF9C4', textColor: '#191D51' };
    }
    return { color: '#191D51', textColor: '#FFFFFF' };
};

const mapEventToCalendar = (event) => {
    const eventDate = new Date(event.dateUtc);
    const isPast = eventDate < new Date();

    let color;
    let textColor;

    if (!isPast) {
        ({ color, textColor } = getLessonColors(event.isFirstLesson));
    } else if (event.status === 1) {
        ({ color, textColor } = getLessonColors(event.isFirstLesson));
    } else if (event.status === 0 || event.status === 3) {
        color = '#F97316';
        textColor = '#FFFFFF';
    } else {
        color = '#DF2B47';
        textColor = '#FFFFFF';
    }

    return {
        id: event._id,
        title: event.fullname,
        start: event.dateUtc,
        color,
        textColor,
        url: process.env.REACT_APP_FRONT_DOMAIN + COURSE_DETAILS_URL + '/' + event._id,
        extendedProps: {
            ...event,
            showCheck: event.status === 1,
        },
    };
};

const Calendar = ({ hideTitle = false }) => {
    const cal = useRef(null);
    const dispatch = useDispatch();
    const { currentProfile } = useSelector((state) => state.profile);
    const [loading, setLoading] = useState(true);

    const isTutor = currentProfile?.role === ProfileRole.TUTOR;
    const preferredHeight = isTutor ? 500 : 960;
    const [contentHeight, setContentHeight] = useState(preferredHeight);

    useEffect(() => {
        const updateHeight = () => {
            const maxHeight = Math.floor(window.innerHeight * 0.8) - 60;
            setContentHeight(Math.min(preferredHeight, maxHeight));
        };
        updateHeight();
        window.addEventListener('resize', updateHeight);
        return () => window.removeEventListener('resize', updateHeight);
    }, [preferredHeight]);

    const handleFetchEvents = useCallback((fetchInfo, successCallback, failureCallback) => {
        if (!currentProfile?._id) {
            failureCallback(new Error('Profile not loaded'));
            return;
        }

        const thunk = isTutor ? getTutorCalendarInfo : getSudentCalendarInfo;
        dispatch(thunk({
            timezone: getDeviceTimeZone(),
            profile: currentProfile._id,
            startDate: fetchInfo.startStr,
            endDate: fetchInfo.endStr,
        }))
            .unwrap()
            .then((result) => {
                successCallback((result.events ?? []).map(mapEventToCalendar));
            })
            .catch(() => {
                failureCallback(new Error('Failed to load events'));
            });
    }, [dispatch, currentProfile, isTutor]);

    const handleEventMouseEnter = (arg) => {
        if (arg.event.extendedProps.disablePointerEvents) {
            arg.el.style.cursor = 'default';
        }
    };

    const handleEventMouseLeave = (arg) => {
        arg.el.style.cursor = '';
    };

    return (
        <div className='grid gap-5 w-full'>
            <div className='w-full bg-white rounded-xl p-5 mt-5 overflow-x-scroll'>
                {!hideTitle && <h2 className='text-nt-blue mb-5'>Calendar</h2>}
                {loading && (
                    <div className='mb-3 text-sm text-nt-grey'>Loading calendar...</div>
                )}
                <FullCalendar
                    viewClassNames='min-w-[600px]'
                    ref={cal}
                    plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
                    initialView='timeGridWeek'
                    allDaySlot={false}
                    eventOverlap={false}
                    headerToolbar={window.innerWidth > 767 ? {
                        left: 'prev,next today',
                        center: 'title',
                        right: 'timeGridWeek,dayGridMonth',
                    } : {
                        left: 'prev,next',
                        right: 'title',
                        center: false,
                    }}
                    firstDay={1}
                    slotMinTime={isTutor ? '08:00:00' : '00:00:00'}
                    slotMaxTime={isTutor ? '22:00:00' : '24:00:00'}
                    editable={false}
                    droppable={false}
                    timeZone='local'
                    contentHeight={contentHeight}
                    defaultTimedEventDuration='00:50:00'
                    handleWindowResize={true}
                    displayEventEnd={false}
                    slotLabelFormat={{
                        hour: '2-digit',
                        hour12: false,
                        minute: '2-digit',
                        meridiem: false,
                    }}
                    eventTimeFormat={{
                        hour: '2-digit',
                        hour12: false,
                        minute: '2-digit',
                        meridiem: false,
                    }}
                    events={handleFetchEvents}
                    loading={setLoading}
                    unselectAuto={false}
                    scrollTimeReset={false}
                    selectable={false}
                    slotDuration='00:60:00'
                    dragScroll={true}
                    slotLabelInterval='00:10:00'
                    eventMouseEnter={handleEventMouseEnter}
                    eventMouseLeave={handleEventMouseLeave}
                    eventContent={(arg) => <CalendarEventContent {...arg} />}
                />
                <CalendarColorLegend variant='student' />
            </div>
        </div>
    );
};

export { Calendar };
